// src/services/vibeSearch.js — searching Vibes.
//
// ══ WHY THIS IS DONE IN THE BROWSER ════════════════════════════════════════
// 🟥 FIRESTORE CANNOT SEARCH TEXT. It has no LIKE, no contains, no relevance —
//    only equality and range on indexed fields. The usual answers are a second
//    system (Algolia/Typesense) or an array-of-keywords field written at post
//    time, and both are a migration: one costs money and a sync job, the other
//    rewrites every existing post and still cannot match a typo or a substring.
//
//    So the corpus comes down once — the most recent posts, the same collection
//    and order the feed already reads — and the matching happens here, where a
//    real ranking function is three functions long instead of a service. It is
//    honest about its limit: it searches what is recent, not all of history.
//    That limit is stated in the UI rather than hidden.
//
// ══ WHAT "SMART" MEANS HERE ════════════════════════════════════════════════
//   · diacritics folded — "cafe" finds "café", "istanbul" finds "İstanbul"
//   · every word must match SOMETHING (AND), so two words narrow instead of widen
//   · a word can match whole, as a prefix, as a substring, or with one typo —
//     in that order of confidence, and the confidence is part of the score
//   · fields are weighted: a link's title beats a caption, a caption beats the
//     name of the track playing under it
//   · the whole phrase appearing intact is worth more than its words scattered
//   · ties break on freshness and engagement, so "today" outranks "last year"
//
// The app has no equivalent — its Vibes search only finds videos — so this is
// web-only on purpose, and nothing here writes anything a phone has to read.

import {
  collection, query, orderBy, limit, getDocs,
  where, getCountFromServer,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { BACKEND_URL } from './socket';
import { fetchPostsByIds } from './vibes';
/* 🏷 the same rules the reels player, the composer and the phone use */
import { postHashtags, hashtagOfQuery } from './videoTags';
/* 🟢 ONE TEST FOR WHAT A POST IS, IMPORTED RATHER THAN RESTATED. The feed card,
   the composer, the search filters and both profile grids all ask the same
   question, and a second copy of the answer is how a poll ends up drawn as a
   poll in the feed and as an empty text square in a grid — which is exactly
   what it did, on the phone, for as long as its grid has existed. */
import { postKind } from './postGrid';
/* 👥 a page post is not a friend's post — see services/friendFeed */
import { scoresAsFriend } from './friendFeed';

/* ── the corpus ──────────────────────────────────────────────────────────── */

const CORPUS_SIZE = 400;
const CORPUS_TTL = 2 * 60 * 1000;      // a search two minutes later is the same search
let cache = { at: 0, posts: [] };

export function corpusLimit() { return CORPUS_SIZE; }

export async function loadSearchCorpus({ force = false } = {}) {
  if (!force && cache.posts.length && Date.now() - cache.at < CORPUS_TTL) return cache.posts;
  const q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(CORPUS_SIZE));
  const snap = await getDocs(q);
  const posts = [];
  snap.forEach((d) => posts.push({ id: d.id, ...d.data() }));
  cache = { at: Date.now(), posts };
  return posts;
}

/** Throw the cache away — used after posting or deleting, so a search is never
 *  answering from a world that has moved on. */
export function invalidateSearchCorpus() { cache = { at: 0, posts: [] }; }

/* ── a hashtag is a different question ─────────────────────────────────────
   🟥 AND IT IS THE ONE QUESTION THIS FILE'S HONEST LIMIT CANNOT ANSWER. The
      corpus is the 400 most recent posts, which is fine for "cafe" — a search
      that says it looks at recent posts and does. A tag is different: tapping
      "#fire" under a reel is a request for that tag's videos, and answering it
      from four hundred rows would show six clips under a heading claiming a
      hundred and twenty. The number and the grid would disagree on one screen.

   🟢 SO A TAG ASKS THE SERVER: an aggregate for the count, one bounded page for
      the results. NO orderBy — `array-contains` on its own is served by the
      automatic single-field index, and adding a sort would need a composite
      index deployed by hand before this could ship. Sorted here instead.

   🟢 AND THE COUNT IS AN AGGREGATE RATHER THAN A STORED COUNTER. A counter
      would have three writers and a delete path, and this product has already
      learnt what an increment that ran twice costs — see followerCountOf. */
export const TAG_PAGE = 60;

export async function loadHashtagPosts(tag, max = TAG_PAGE) {
  const t = String(tag || '').trim().toLowerCase();
  if (!t) return { posts: [], count: 0 };
  const base = query(collection(db, 'posts'), where('hashtags', 'array-contains', t));
  const snap = await getDocs(query(base, limit(max)));
  const posts = [];
  snap.forEach((d) => posts.push({ id: d.id, ...d.data() }));
  const ms = (x) => (x?.timestamp?.toDate ? x.timestamp.toDate().getTime()
    : (x?.timestamp?.seconds ? x.timestamp.seconds * 1000 : 0));
  posts.sort((a, b) => ms(b) - ms(a));
  const agg = await getCountFromServer(base);
  return { posts, count: agg.data().count };
}

/* ── text ────────────────────────────────────────────────────────────────── */

/** lowercase, accent-folded, punctuation-flattened. The one place text is
 *  normalised, so the query and the post are always folded identically. */
export function fold(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')     // strip the combining marks NFD split off
    .toLowerCase()
    .replace(/[\u2019']/g, '')           // don't → dont, so either spelling hits
    /* keep letters and digits from the alphabets this app is actually used in —
       Latin, Arabic, Cyrillic, CJK — and turn everything else into a space */
    .replace(/[^a-z0-9\u0600-\u06ff\u0400-\u04ff\u4e00-\u9fff]+/g, ' ')
    .trim();
}

/** Are these one edit apart? Cheap enough to run per token, unlike a full
 *  Levenshtein matrix — it bails the moment a second difference shows up. */
function within1(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (la > lb) i++;
    else if (lb > la) j++;
    else { i++; j++; }
  }
  return true;
}

/* ── what a post is ──────────────────────────────────────────────────────── */

/**
 * 🟥 A POLL IS A POLL FIRST. It used to fall through every test and land on
 *    'text', and a poll's `content` is usually empty — the question lives in
 *    `pollQuestion` — so the profile grid drew a blank square for it and the
 *    Text filter claimed a poll was text. The check goes FIRST, above media,
 *    because a poll with a picture attached is still a poll: the question is
 *    the post, which is the same reason the feed card draws the poll above the
 *    media rather than below it.
 */
/* 🟢 ONE RULE, TWO NAMES. This answered "what kind of post" for the search
   filters, and `postKind` answered it for the grid tiles — the same five
   answers computed twice, in two files, which is how the phone's grid ended up
   drawing a poll as a text post while the browser's did not. `kindOf` keeps its
   name because the filters read well with it, and delegates. */
export function kindOf(p) {
  return postKind(p);
}

export const KINDS = [
  { id: 'all', name: 'Everything' },
  { id: 'video', name: 'Videos' },
  { id: 'photo', name: 'Photos' },
  { id: 'poll', name: 'Polls' },
  { id: 'link', name: 'Links' },
  { id: 'text', name: 'Text' },
];

/* ── the fields, and what each is worth ──────────────────────────────────────
   🟢 A LINK'S TITLE OUTRANKS A CAPTION, and a caption outranks the name of the
      song playing underneath. Someone searching "wwdc" means the article called
      that far more often than they mean a post whose backing track happens to
      share the word. */
function fieldsOf(p) {
  const out = [];
  const add = (text, weight) => {
    const t = fold(text);
    if (t) out.push({ t, toks: t.split(' '), w: weight });
  };
  add(p.linkTitle, 4.5);
  add(p.linkDomain || p.linkUrl, 3);
  add(p.content, 3.2);
  add(p.userName, 3);
  add(p.userUsername || p.username, 3);
  add(p.pageName, 2.4);
  add(p.pageCategory, 1.6);
  add(p.locationLabel, 2);
  add(p.audioTrackTitle, 1.8);
  add(p.status?.location || p.status?.to || p.status?.from, 1.6);
  /* 🏷 🟥 THE TAGS WERE INVISIBLE TO SEARCH. A clip captioned only "#fire" has
     an empty-ish `content` once the hash is folded away, so searching the word
     "fire" missed exactly the posts filed under it. Weighted just above a
     caption: a tag is a word somebody chose to file the post under, which is a
     stronger statement of what it is about than the same word in a sentence. */
  add(postHashtags(p).join(' '), 3.4);
  return out;
}

/** 0 → this field does not contain the term at all. */
function fieldScore(f, term) {
  if (f.t === term) return 1;
  let best = 0;
  for (const tok of f.toks) {
    if (tok === term) return 1;                                  // whole word
    if (tok.startsWith(term)) { best = Math.max(best, 0.82); continue; }   // typing
    if (term.length >= 4 && within1(tok, term)) best = Math.max(best, 0.62); // typo
  }
  if (!best && term.length >= 3 && f.t.includes(term)) best = 0.5;   // inside a word
  return best;
}

/**
 * Rank posts against a query.
 * @param posts   the corpus (already filtered for blocked/hidden by the caller)
 * @param raw     what the user typed
 * @param kind    one of KINDS
 * @param opts    { friends:Set, now:number }
 */
export function searchPosts(posts, raw, kind = 'all', { friends, now = Date.now() } = {}) {
  const phrase = fold(raw);
  const terms = phrase.split(' ').filter(Boolean);
  if (!terms.length) return [];

  /* 🟥 A TAG IS AN EXACT MATCH, NOT A RANKING. "#fire" typed into the box means
     the tag, and fuzzy-matching it would drag in every caption containing the
     word — the opposite of what a tag is for. The server path
     (loadHashtagPosts) is the one a chip tap takes; this is what happens when
     somebody types one, over whatever corpus the caller passed. */
  const only = hashtagOfQuery(raw);

  const out = [];
  for (const p of posts) {
    if (kind !== 'all' && kindOf(p) !== kind) continue;
    if (only && postHashtags(p).indexOf(only) < 0) continue;
    const fields = fieldsOf(p);
    if (!fields.length) continue;

    let total = 0;
    let ok = true;
    for (const term of terms) {
      let best = 0;
      for (const f of fields) {
        const s = fieldScore(f, term) * f.w;
        if (s > best) best = s;
      }
      /* 🟥 EVERY WORD HAS TO LAND SOMEWHERE. An OR search feels clever for one
         query and useless for the next: type two words and you get everything
         that matched either, which is more results and worse ones. */
      if (best <= 0) { ok = false; break; }
      total += best;
    }
    if (!ok) continue;

    // the phrase intact, in one field, is a much stronger signal than its words
    if (terms.length > 1 && fields.some((f) => f.t.includes(phrase))) total += 6;

    const ts = p.timestamp?.toDate ? p.timestamp.toDate().getTime() : now;
    const days = Math.max(0, (now - ts) / 864e5);
    total += 3 / (1 + days / 7);                       // fresh, gently
    const eng = Math.max(p.likes?.length || 0, Number(p.likesV2 === true ? p.likesCount : 0) || 0) + Math.max(p.comments?.length || 0, Number(p.commentsCount) || 0) * 2 + (p.sharesCount || 0) * 3;
    total += Math.min(3, eng / 25);                    // and popular, gently
    if (scoresAsFriend(p, friends)) total += 1.5;      // people you actually know, never their pages

    out.push({ ...p, _ts: ts, _score: total, _kind: kindOf(p) });
  }

  out.sort((a, b) => b._score - a._score || b._ts - a._ts);
  return out.slice(0, 120);
}

/** The distinct people behind a set of results, best match first. */
export function peopleFrom(results, max = 8) {
  const seen = new Map();
  for (const p of results) {
    if (!p.userId || seen.has(p.userId)) continue;
    seen.set(p.userId, {
      uid: p.userId,
      name: p.userName || 'Allo user',
      image: p.userImage || null,
      pageId: p.authorType === 'page' ? p.pageId : undefined,
    });
    if (seen.size >= max) break;
  }
  return Array.from(seen.values());
}

/** Split text into [{text, hit}] so the matched words can be marked up.
 *  Folding changes length (accents), so matching is done on a folded COPY and
 *  the slices are taken from the original — otherwise the highlight would drift
 *  by one character per accent in the string. */
export function highlightParts(text, raw) {
  const src = String(text || '');
  const terms = fold(raw).split(' ').filter((t) => t.length >= 2);
  if (!src || !terms.length) return [{ text: src, hit: false }];

  const foldedChars = [];
  for (let i = 0; i < src.length; i++) {
    const f = fold(src[i]);
    foldedChars.push(f === '' ? ' ' : f[0]);     // 1 char in, 1 char out — always
  }
  const folded = foldedChars.join('');

  const marks = new Array(src.length).fill(false);
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const at = folded.indexOf(term, from);
      if (at < 0) break;
      for (let i = at; i < at + term.length && i < marks.length; i++) marks[i] = true;
      from = at + term.length;
    }
  }

  const parts = [];
  let cur = null;
  for (let i = 0; i < src.length; i++) {
    if (!cur || cur.hit !== marks[i]) { cur = { text: '', hit: marks[i] }; parts.push(cur); }
    cur.text += src[i];
  }
  return parts;
}

/* ══ 🔎 THE SERVER'S SEARCH ═════════════════════════════════════════════════
   🟥 THE CORPUS ABOVE IS THE NEWEST 400 POSTS, FILTERED HERE — and "filtered
      here" included friends-only posts of strangers, kept off the page only by
      this tab's good manners. The server answers now (searchRoutes.js): every
      post it returns is one this account may see, people come from the whole
      directory (full names too), and the same ranking runs (services/searchRank).
   🟢 The corpus stays as the fallback when the server cannot be reached. */
/** 👥 "Friend · 22 mutual friends" — the counts behind each name in Tag people
    (searchRoutes.js mutual). Only counts; a stranger's friend list never leaves the server. */
export async function mutualFriends(uids) {
  const want = Array.from(new Set((uids || []).filter(Boolean))).slice(0, 60);
  const u = auth.currentUser;
  if (!want.length || !u || u.isAnonymous) return {};
  try {
    const res = await fetch(`${BACKEND_URL}/api/people/mutual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
      body: JSON.stringify({ uids: want }),
    });
    if (!res.ok) return {};
    const j = await res.json();
    return j && j.ok && j.meta && typeof j.meta === 'object' ? j.meta : {};
  } catch { return {}; }
}

export async function serverSearch(q, kind = 'all') {
  const u = auth.currentUser;
  if (!u || u.isAnonymous) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/vibes/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
      body: JSON.stringify({ q, kind }),
    });
    if (!res.ok) return null;
    const j = await res.json();
    if (!j || !j.ok) return null;
    const ids = Array.isArray(j.postIds) ? j.postIds : [];
    const found = ids.length ? await fetchPostsByIds(ids) : [];
    const byId = new Map((found || []).map((p) => [p.id, p]));
    const posts = ids.map((id) => byId.get(id)).filter(Boolean);
    const people = (Array.isArray(j.people) ? j.people : []).map((p) => ({
      uid: p.uid, name: p.name || p.username || 'Allo user', image: p.image || null,
      ...(p.pageId ? { pageId: p.pageId } : {}),
    }));
    return { posts, people };
  } catch { return null; }
}
